import * as React from 'react'
import {
  makeStyles,
  createStyles,
  Theme,
} from 'cs-kevlar-react/lib/es/theming/theme'
import { Table, SearchInput, Divider } from 'cs-kevlar-react'
import { ColumnDef } from 'cs-kevlar-react/lib/es/table/base'
import { Grid } from 'cs-kevlar-react'
import { IdentifiableModel } from 'cs-kevlar-react/lib/es/base'
import {
  PaginationOptions,
} from 'cs-kevlar-react/lib/es/table/Table'

interface TablePickerProps<TType extends IdentifiableModel, TKey> {
  availableItems: TType[]
  columns: ColumnDef<TType, TKey>[]
  onSelect: (id: string) => void
  searchLabel: string
  caseSensitiveSearch?: boolean
  ariaLabel: string
  uniqueIdentifier: string
  singularAdjective: string
  pluralAdjective: string
}

const PAGINATION_OPTIONS: Partial<PaginationOptions> = {
  hidePaginationBar: true,
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {},
    gridItem: {
      width: '100%',
    },
    clickable: {
      cursor: 'pointer',
    },
    searchInput: {
      boxShadow: 'none',
      borderRadius: 0,
      width: '100%',
    },
    searchDivider: {
      height: 1,
    },
  }),
)

export const TablePicker = <TType extends IdentifiableModel, TKey>({
  availableItems,
  onSelect,
  columns,
  searchLabel,
  caseSensitiveSearch = false,
  ariaLabel,
  uniqueIdentifier,
  singularAdjective,
  pluralAdjective,
}: TablePickerProps<TType, TKey>) => {
  const classes = useStyles({})
  const [search, setSearch] = React.useState<string[]>([])

  const filteredItems = availableItems.filter(item => {
    const matchedProps = columns.reduce((accum, current) => {
      if (!current.filterable || !current.dataProperty)
        return {
          ...accum,
        }

      const propValue = (item as any)[current.dataProperty as string] as any

      if (search.length === 0)
        return {
          ...accum,
        }

      if (
        propValue &&
        (propValue.toString().indexOf(search[0]) > -1 ||
          (!caseSensitiveSearch &&
            propValue
              .toString()
              .toLowerCase()
              .indexOf(search[0].toLowerCase()) > -1))
      ) {
        return {
          ...accum,
          [current.dataProperty as string]: true,
        }
      }

      return {
        ...accum,
      }
    }, {})
    return search[0] !== '' && Object.keys(matchedProps).length > 0
  })

  const paginationOptions = React.useMemo(() => {
    return {
      ...PAGINATION_OPTIONS,
      singularAdjective,
      pluralAdjective,
    }
  }, [singularAdjective, pluralAdjective])

  return (
    <Grid
      container
      direction="column"
      alignItems="flex-start"
      justifyContent="center"
      className={classes.root}
    >
      <Grid item className={classes.gridItem}>
        <SearchInput
          className={classes.searchInput}
          onChange={setSearch}
          placeholderText={searchLabel}
          enableAutomaticSearching
          id={`${uniqueIdentifier}-searchinput`}
        />
        <Divider className={classes.searchDivider} />
      </Grid>
      <Grid item className={classes.gridItem}>
        <Table
          data={filteredItems}
          columns={columns}
          ariaLabel={ariaLabel}
          paginationOptions={paginationOptions}
          uniqueIdentifier={uniqueIdentifier}
          useDefaultRow
          onRowClicked={(row) => onSelect(row.id)}
        />
      </Grid>
    </Grid>
  );
}
